import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTheme, usePageTitle, AlertBanner } from '@flowcore-water/ui';
import { fetchJoin, type JoinResult } from '../lib/api';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';

export function JoinView() {
  const { joinKey } = useParams<{ joinKey: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTheme();
  usePageTitle('ST Mirror', 'Related Records');

  const id = Number(searchParams.get('id'));
  const page = Number(searchParams.get('page')) || 1;

  const [data, setData] = useState<JoinResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!joinKey || !id) return;
    setLoading(true);
    fetchJoin(joinKey, id, { page })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [joinKey, id, page]);

  useEffect(() => { load(); }, [load]);

  if (!joinKey || !id) {
    return (
      <div className="flex h-48 items-center justify-center">
        <span style={{ color: t.textMuted }}>Missing join key or source ID</span>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <div className="mb-4 flex items-center gap-2 text-sm" style={{ color: t.textMuted }}>
        <Link to="/" style={{ color: t.accent }}>Entities</Link>
        <span>/</span>
        {data && (
          <>
            <Link to={`/browse/${data.from_entity}/${id}`} style={{ color: t.accent }}>
              {data.from_entity} #{id}
            </Link>
            <span>/</span>
          </>
        )}
        <span style={{ color: t.textPrimary }}>{data?.display ?? joinKey}</span>
      </div>

      <div className="mb-4">
        <h1 className="text-xl font-bold tracking-tight" style={{ color: t.textPrimary }}>
          {data?.display ?? joinKey}
        </h1>
        {data && (
          <p className="mt-0.5 text-xs" style={{ color: t.textMuted }}>
            {data.total} records from {data.from_entity} #{id}
          </p>
        )}
      </div>

      {error && <AlertBanner variant="error" message={error} />}

      {loading && !data && (
        <div className="flex h-48 items-center justify-center">
          <span style={{ color: t.textMuted }}>Loading...</span>
        </div>
      )}

      {data && (
        <>
          <DataTable
            columns={data.data.length > 0 ? Object.keys(data.data[0]) : []}
            rows={data.data}
            onRowClick={(row) => {
              if (data.to_entity !== 'fleet_vehicles') {
                navigate(`/browse/${data.to_entity}/${row.id}`);
              }
            }}
          />
          <Pagination
            page={data.page}
            pages={data.pages}
            total={data.total}
            onPageChange={(p) => {
              const next = new URLSearchParams(searchParams);
              next.set('page', String(p));
              setSearchParams(next);
            }}
          />
        </>
      )}
    </div>
  );
}
